import { FailureReasonType, HelpKind } from "./task";

/**
 * Presentation metadata for every task failure reason, shared across the
 * session-runs, session-merchants (analytics) and tasks pages so a reason is
 * rendered the same way everywhere — a readable label and a consistent colour
 * — instead of the raw enum string (e.g. "checkout_url_mismatch").
 *
 * `category` groups reasons for reporting:
 *  - template : the merchant's template broke
 *  - session  : the captured session couldn't be used
 *  - infra    : our own infrastructure/engine fault (not the template)
 *  - bot      : an anti-automation wall blocked the run
 *  - coupons  : nothing to test / couldn't fetch codes
 *  - llm      : agentic LLM limits
 *  - capture  : headless template-capture run's own circuit breakers (budget/timeout/llm-errors)
 *  - other    : uncategorised
 *
 * `bg`/`text` are react-bootstrap Badge variants.
 */
export type TFailureReasonCategory =
  | "template"
  | "session"
  | "infra"
  | "bot"
  | "coupons"
  | "llm"
  | "capture"
  /** The merchant's own data is the blocker — nothing in the run or the template
   *  is broken and no retry will help; a person has to change something. */
  | "merchant"
  | "other";

export interface IFailureReasonMeta {
  label: string;
  category: TFailureReasonCategory;
  bg: string;
  text?: "dark" | "light";
}

export const FAILURE_REASON_META: Record<
  FailureReasonType,
  IFailureReasonMeta
> = {
  [FailureReasonType.BOT_DETECTION]: {
    label: "Bot detection",
    category: "bot",
    bg: "info",
    text: "dark",
  },
  [FailureReasonType.LOGIN_REQUIRED]: {
    label: "Login required",
    category: "bot",
    bg: "info",
    text: "dark",
  },
  [FailureReasonType.SITE_UNREACHABLE]: {
    label: "Site unreachable",
    category: "bot",
    bg: "secondary",
    text: "light",
  },
  [FailureReasonType.PROXY_UNAVAILABLE]: {
    label: "Proxy unavailable",
    category: "infra",
    bg: "secondary",
    text: "light",
  },
  [FailureReasonType.NO_PURCHASABLE_PRODUCT]: {
    label: "No purchasable product",
    category: "merchant",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.NO_COUPONS_FOUND]: {
    label: "No coupons found",
    category: "coupons",
    bg: "light",
    text: "dark",
  },
  [FailureReasonType.COUPON_FETCH_FAILED]: {
    label: "Coupon fetch failed",
    category: "coupons",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.EXCEEDED_LLM_AGENT_MAX_LIMITS]: {
    label: "LLM agent limits exceeded",
    category: "llm",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.BROWSER_LAUNCH_FAILURE]: {
    label: "Browser launch failure",
    category: "infra",
    bg: "dark",
  },
  [FailureReasonType.LLM_CONTEXT_LENGTH_EXCEEDED]: {
    label: "LLM context length exceeded",
    category: "llm",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.UNEXPECTED_ERROR]: {
    label: "Unexpected error",
    category: "other",
    bg: "secondary",
  },
  [FailureReasonType.TEMPLATE_ERRORS]: {
    label: "Template errors",
    category: "template",
    bg: "danger",
  },
  [FailureReasonType.NO_CAPTURED_SESSION]: {
    label: "No captured session",
    category: "session",
    bg: "secondary",
  },
  [FailureReasonType.SESSION_TOO_OLD]: {
    label: "Session too old",
    category: "session",
    bg: "secondary",
  },
  [FailureReasonType.SESSION_EXPIRED]: {
    label: "Session expired",
    category: "session",
    bg: "secondary",
  },
  [FailureReasonType.SESSION_CART_EMPTY]: {
    label: "Cart empty",
    category: "session",
    bg: "secondary",
  },
  [FailureReasonType.SESSION_NAVIGATION_FAILED]: {
    label: "Session navigation failed",
    category: "session",
    bg: "secondary",
  },
  [FailureReasonType.CHECKOUT_URL_MISMATCH]: {
    label: "Checkout URL mismatch",
    category: "session",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.NO_LIVE_TEMPLATE]: {
    label: "No live template",
    category: "template",
    bg: "light",
    text: "dark",
  },
  [FailureReasonType.SKU_TEMPLATE_FAILED]: {
    label: "SKU template failed",
    category: "template",
    bg: "danger",
  },
  [FailureReasonType.DATABASE_ERROR]: {
    label: "Database error",
    category: "infra",
    bg: "dark",
  },
  [FailureReasonType.ENGINE_INJECTION_FAILED]: {
    label: "Engine injection failed",
    category: "infra",
    bg: "dark",
  },
  [FailureReasonType.PROTOCOL_TIMEOUT]: {
    label: "Protocol timeout",
    category: "infra",
    bg: "dark",
  },
  [FailureReasonType.CAPTURE_BUDGET_EXCEEDED]: {
    label: "Capture budget exceeded",
    category: "capture",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.CAPTURE_LLM_ERRORS_EXCEEDED]: {
    label: "Capture LLM errors exceeded",
    category: "capture",
    bg: "danger",
  },
  [FailureReasonType.CAPTURE_ENGINE_TIMEOUT]: {
    label: "Capture engine timeout",
    category: "capture",
    bg: "dark",
  },
  [FailureReasonType.SITE_NOT_SUITABLE]: {
    label: "Site not suitable",
    category: "merchant",
    bg: "light",
    text: "dark",
  },
  [FailureReasonType.COUPON_FORM_NOT_FOUND]: {
    label: "Coupon form not found",
    category: "template",
    bg: "warning",
    text: "dark",
  },
  [FailureReasonType.CHECKOUT_NOT_REACHED]: {
    label: "Checkout not reached",
    category: "template",
    bg: "warning",
    text: "dark",
  },
};

export const HELP_KIND_META: Record<
  HelpKind,
  { label: string; description: string }
> = {
  [HelpKind.SIGN_IN]: {
    label: "Sign in",
    description:
      "Checkout sits behind a login. Sign in with Kindred's test account and capture the session.",
  },
  [HelpKind.BOT_WALL]: {
    label: "Bot wall",
    description:
      "The site served a challenge page. A session captured past it may get the walk through.",
  },
  [HelpKind.TEACH_JOURNEY]: {
    label: "Show the way",
    description:
      "The walk could not find its way to the coupon field. Show it the product, basket, checkout and coupon field.",
  },
  [HelpKind.NOT_SUITABLE]: {
    label: "Not suitable",
    description:
      "Classed as a site we don't test. Decide whether to run it anyway.",
  },
  [HelpKind.TEST_PRODUCT]: {
    label: "Needs a test product",
    description:
      "Every product the walk reached was out of stock. Give it one that can be bought.",
  },
  [HelpKind.TEMPLATE]: {
    label: "Template broke",
    description:
      "The stored template or route stopped working. Rebuild or fix it with the extension; if the tool itself can't, ask for help and a lead sends it to a dev.",
  },
  [HelpKind.INFRA]: {
    label: "Run errored",
    description:
      "Proxy, crash, timeout or AI limits. Check the site loads for you and retest; if it keeps erroring, ask for help and a lead sends it to a dev.",
  },
  [HelpKind.DATA]: {
    label: "Data",
    description: "Nothing to test, or upstream data was missing.",
  },
};

/** Title-cases an unmapped raw reason, e.g. "some_new_reason" → "Some new reason". */
const titleCaseRaw = (raw: string): string => {
  const spaced = raw.replace(/_/g, " ").trim();
  if (!spaced) return "Unknown";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Human-readable label for a failure reason. Tolerant of the raw values that
 * arrive from the API: known enum values map via {@link FAILURE_REASON_META},
 * "unknown"/null/empty become "Unknown", and any other string is title-cased
 * so a newly-added reason still reads sensibly before this map is updated.
 */
export const failureReasonLabel = (
  reason: string | null | undefined
): string => {
  if (!reason || reason === "unknown") return "Unknown";
  const meta = FAILURE_REASON_META[reason as FailureReasonType];
  return meta ? meta.label : titleCaseRaw(reason);
};

/** Badge variant for a failure reason, falling back to a neutral secondary. */
export const failureReasonBadge = (
  reason: string | null | undefined
): { bg: string; text?: "dark" | "light" } => {
  const meta = reason
    ? FAILURE_REASON_META[reason as FailureReasonType]
    : undefined;
  return meta ? { bg: meta.bg, text: meta.text } : { bg: "secondary" };
};
